import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Button, useAiChat } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Archive, Delete, LayersFront } from '@factorialco/f0-react/icons/app'
import {
  EngagementSurveysDataCollectionDocument,
  EngagementSurveysListPageDocument,
  FormsEnumsStatusEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities'
import i18n from 'modules/core/lib/i18n'

import { useOne } from 'modules/ai/lib/useOne'
import { useCreateSurvey } from 'modules/engagement_surveys/hooks/useCreateSurvey'
import { getFormStatusFilter } from 'modules/engagement_surveys/lib/filters'
import {
  formRecurrenceMapper,
  mapRecurrenceTypeFromBackend,
} from 'modules/engagement_surveys/lib/formRecurrenceMapper'
import { formStatusMapper } from 'modules/engagement_surveys/lib/formStatusMapper'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

const DEFAULT_SURVEY_STATUS_FILTERS: FormsEnumsStatusEnum[] = [
  'active',
  'draft',
  'scheduled',
  'finished',
]

export default function SurveysListPage() {
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { openActivity } = useActivities()
  const { data: pageData, loading, error } = useQuery(EngagementSurveysListPageDocument, {
    variables: {
      companyId: company.id,
    },
  })
  const devAiAgentEnabled =
    pageData?.features?.featuresConnection?.nodes.find(
      (node) => node.name === 'dev_ai_agent'
    )?.enabled ?? false

  const canCreateSurvey = pageData?.surveys._policies.surveysCreateSurvey.can ?? false
  const createSurvey = useCreateSurvey()

  const { open: isChatOpen, clear: clearChat } = useAiChat()
  const [fullscreen, setFullscreen] = useState(false)
  const [autoOpenChat, setChatOpen] = useState(false)
  const shouldClearOnNextOpen = useRef(false)

  useEffect(() => {
    if (!isChatOpen) {
      setChatOpen(false)
    }
  }, [isChatOpen])

  useEffect(() => {
    if (isChatOpen && shouldClearOnNextOpen.current) {
      shouldClearOnNextOpen.current = false
      clearChat()
    }
  }, [isChatOpen, clearChat])

  const createFromScratch = useCallback(async () => {
    const surveyId = await createSurvey()
    if (surveyId) {
      navigate(
        navigation.engagement.smart_surveys.survey.questions.fullPath({
          surveyId,
        })
      )
    }
  }, [createSurvey, navigate, navigation.engagement.smart_surveys.survey.questions])

  const oneOptions = useMemo(
    () => ({
      initialMessage: i18n.t('forms.one.chat_initial_message_new'),
      autoOpenChat,
      fullscreen,
      footer: canCreateSurvey ? (
        <F0Button
          label={i18n.t('surveys.new_create_survey_modal.skip_label')}
          onClick={createFromScratch}
          variant="ghost"
        />
      ) : undefined,
    }),
    [autoOpenChat, fullscreen, canCreateSurvey, createFromScratch]
  )
  useOne<null>(['surveys'], null, oneOptions)

  const handleCreateSurvey = async () => {
    if (devAiAgentEnabled) {
      shouldClearOnNextOpen.current = true
      setFullscreen(true)
      setChatOpen(true)
    } else {
      createFromScratch()
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: EngagementSurveysDataCollectionDocument,
    mapData: (data) => {
      return data.surveys.surveysConnection.edges.map((edge) => edge.node)
    },
    mapPagination: (data) => ({
      total: Number(data.surveys.surveysConnection.totalCount),
    }),
    mapVariables: ({ filters, search }) => ({
      status:
        filters.status && filters.status.length > 0
          ? filters.status.filter(Boolean)
          : DEFAULT_SURVEY_STATUS_FILTERS,
      search,
    }),
    search: {
      enabled: true,
    },
    filters: {
      status: getFormStatusFilter(),
    },
    itemUrl(item) {
      return navigation.engagement.smart_surveys.survey.fullPath({
        surveyId: item.id,
      })
    },
    primaryActions: () => {
      if (canCreateSurvey) {
        return {
          label: i18n.t('surveys.surveys_table.actions.create'),
          icon: Add,
          onClick: () => {
            handleCreateSurvey()
          },
        }
      }

      return undefined
    },
    itemActions: ({ id, form }) => {
      const actions = []

      if (form.status === 'finished') {
        actions.push({
          label: i18n.t('common.archive'),
          icon: Archive,
          onClick: () => {
            openActivity('engagement_surveys.archiveSurvey', { surveyId: id.toString() })
          },
        })

        actions.push({ type: 'separator' as const })
      }

      if (canCreateSurvey) {
        actions.push({
          label: i18n.t('common.duplicate'),
          icon: LayersFront,
          onClick: () => {
            openActivity('engagement_surveys.duplicateSurvey', { surveyId: id.toString() })
          },
        })
      }

      if (form.status === 'draft') {
        actions.push({
          critical: true,
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => {
            openActivity('engagement_surveys.deleteSurvey', { surveyId: id.toString() })
          },
        })
      }

      return actions
    },
  })

  return (
    <ResponsivePage.Body variant="standard" loading={loading} error={error}>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('surveys.surveys_table.columns.title'),
                  render: (item) => {
                    return item.form.title
                  },
                },
                {
                  label: i18n.t('surveys.surveys_table.columns.status'),
                  render: (item) => ({
                    type: 'status',
                    value: formStatusMapper(item.form.status),
                  }),
                },
                {
                  label: i18n.t('surveys.surveys_table.columns.recurrence'),
                  render: (item) => {
                    // Prefer form.recurrenceType (new), fallback to schedule.recurrenceType (legacy)
                    const recurrenceType =
                      item.form.recurrenceType ?? item.form.schedule?.recurrenceType ?? null
                    return formRecurrenceMapper(mapRecurrenceTypeFromBackend(recurrenceType))
                  },
                },
                {
                  label: i18n.t('surveys.surveys_table.columns.participation'),
                  render: (item) => {
                    if (['scheduled', 'draft'].includes(item.form.status)) {
                      return '-'
                    }

                    const questions = Number(item.form.sentCount)
                    const answers = Number(item.form.answeredCount)
                    const percentage = Number(((answers / questions) * 100).toFixed(1))

                    return {
                      type: 'percentage',
                      value: {
                        label: `${answers}/${questions}`,
                        percentage,
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
        emptyStates={{
          'no-data': {
            title: i18n.t('surveys.surveys_table.empty_states.no_data.title'),
            description: i18n.t('surveys.surveys_table.empty_states.no_data.description'),
            actions: canCreateSurvey
              ? [
                  {
                    label: i18n.t('surveys.surveys_table.empty_states.no_data.create_action'),
                    onClick: () => {
                      handleCreateSurvey()
                    },
                  },
                ]
              : undefined,
          },
        }}
      />
    </ResponsivePage.Body>
  )
}
